import { isSignInRoute } from '@common/url';
import { removeQueryParam } from '@prophecy/utils/history';
import { appendQueryParams } from '@prophecy/utils/url';

import {
  getAllIdpTypes,
  isHttpUrl,
  oAuthAzureADAuthenticate,
  oAuthGoogleAuthenticate,
  oAuthSamlAuthenticate
} from '../../../data/apis/api';
import { AuthTypes } from '../../../data/apis/types';
import { clearSsoLoginHint, hasSsoLoginHint, isSsoAutoLoginEnabled, markSsoAttempt, saveSsoReturnUrl } from './state';

import type { OAuthGoogleAuthenticatePayload } from '../../../data/apis/types';

const AUTO_LOGIN_PARAM = 'autoLogin';

const GATE_TIMEOUT_MS = 8000;

export type AutoLoginConnector = { id: string; type: AuthTypes };

type OAuthResponse = { action: string; SAMLRequest?: string; RelayState?: string };

const EMPTY_OAUTH_PAYLOAD: OAuthGoogleAuthenticatePayload = { productType: undefined };

// One budget for the whole gate, not per call, so a slow backend cannot hold the splash for a multiple of it.
const withDeadline = async <T>(work: Promise<T>, deadline: number) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), Math.max(deadline - Date.now(), 0));
  });
  try {
    return await Promise.race([work, expiry]);
  } finally {
    clearTimeout(timer);
  }
};

const getReturnTarget = () => {
  const { pathname, search, href } = window.location;
  return isSignInRoute(pathname) ? new URLSearchParams(search).get('redirect') || undefined : href;
};

// Only the exact value `false`; anything else would be a crafted-link redirect to the IDP.
export function applyAutoLoginBypass(search: string) {
  if (new URLSearchParams(search).get(AUTO_LOGIN_PARAM) !== 'false') return false;
  clearSsoLoginHint();
  removeQueryParam(AUTO_LOGIN_PARAM);
  return true;
}

async function findConnector(deadline: number) {
  if (!isSsoAutoLoginEnabled()) return undefined;
  if (!hasSsoLoginHint()) return undefined;

  const resp = await withDeadline(
    getAllIdpTypes() as Promise<{ data?: { autoLoginConnector?: AutoLoginConnector } }>,
    deadline
  );
  return resp?.data?.autoLoginConnector;
}

async function resolveAuthUrl(connector: AutoLoginConnector, deadline: number) {
  switch (connector.type) {
    case AuthTypes.saml: {
      const resp = await withDeadline(oAuthSamlAuthenticate({}) as Promise<OAuthResponse>, deadline);
      // appendQueryParams on an empty action returns a truthy relative string.
      if (!resp?.action || !resp.SAMLRequest) return undefined;
      return appendQueryParams(resp.action, { SAMLRequest: resp.SAMLRequest, RelayState: resp.RelayState ?? '' });
    }
    case AuthTypes.azuread:
    case AuthTypes.google: {
      const start = connector.type === AuthTypes.azuread ? oAuthAzureADAuthenticate : oAuthGoogleAuthenticate;
      const resp = await withDeadline(start(EMPTY_OAUTH_PAYLOAD) as Promise<OAuthResponse>, deadline);
      return resp?.action || undefined;
    }
    default:
      return undefined;
  }
}

// Only this module reaches api.ts; ./state is the half api.ts imports, so it must stay import-free.
export async function runSsoAutoLoginGate() {
  try {
    const deadline = Date.now() + GATE_TIMEOUT_MS;
    const connector = await findConnector(deadline);
    if (!connector) return false;

    const url = await resolveAuthUrl(connector, deadline);
    if (!url || !isHttpUrl(url)) return false;

    markSsoAttempt();
    saveSsoReturnUrl(getReturnTarget());
    window.location.replace(url);
    return true;
  } catch {
    return false;
  }
}
